
import { memo } from 'react'
import { Panel } from '@xyflow/react'
import { Icon } from '@/components/ui/Icon'
import { SIGNAL_CATEGORIES, SIGNAL_META, type SignalCategory } from '@/lib/signals'
import { CROSSING_LABELS, CROSSING_STYLES, isCrossingStyle, type CrossingStyle } from '@/lib/cable-route'
import { btn, cx, inputSm } from '@/lib/ui'

type SignalLegendProps = {
  visibleSignals: Record<SignalCategory, boolean>
  counts: Record<SignalCategory, number>
  onToggle: (signal: SignalCategory) => void
  onShowAll: () => void
  /** Arrowheads at the input end of every cable. */
  arrows: boolean
  onToggleArrows: () => void
  /** How a cable is drawn where it crosses another. */
  crossings: CrossingStyle
  onCrossingsChange: (style: CrossingStyle) => void
}

/**
 * The crossing style as a tiny drawing of it, like the signal swatches: a
 * level line, and an upright one giving way in the chosen style.
 */
function CrossingGlyph({ style }: { style: CrossingStyle }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 14 14"
      className="h-3.5 w-3.5 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    >
      <path d="M0.75 7 H13.25" />
      {style === 'hop' && <path d="M7 0.75 V3.5 A3.5 3.5 0 0 1 7 10.5 V13.25" />}
      {style === 'caps' && <path d="M7 0.75 V4 M7 10 V13.25 M5 4 H9 M5 10 H9" />}
      {style === 'off' && <path d="M7 0.75 V13.25" />}
    </svg>
  )
}

/**
 * The always-visible signal legend: one toggle per signal category, pinned to
 * the drawing surface as a React Flow panel so it survives every viewport
 * width and stays put whatever is selected. Each row is a real button whose
 * text reads "<label> <count>", so assistive tech addresses it by the signal's
 * name. No margin utilities: React Flow's own un-layered panel CSS sets them.
 */
function SignalLegendComponent({
  visibleSignals,
  counts,
  onToggle,
  onShowAll,
  arrows,
  onToggleArrows,
  crossings,
  onCrossingsChange,
}: SignalLegendProps) {
  const allOn = SIGNAL_CATEGORIES.every((signal) => visibleSignals[signal])
  return (
    <Panel
      position="top-left"
      role="group"
      aria-label="Signal legend"
      className="glass w-max min-w-40 rounded-2xl p-1.5"
    >
      <p className="px-2 pb-1 pt-0.5 text-[10px] font-medium uppercase tracking-[0.08em] text-muted">
        Signals
      </p>
      <ul className="space-y-0.5">
        {SIGNAL_CATEGORIES.map((signal) => {
          const meta = SIGNAL_META[signal]
          const on = visibleSignals[signal]
          const count = counts[signal]
          return (
            <li key={signal}>
              <button
                type="button"
                aria-pressed={on}
                onClick={() => onToggle(signal)}
                title={`${on ? 'Hide' : 'Show'} ${meta.label} — ${count} cable${count === 1 ? '' : 's'}`}
                className={cx(
                  'flex w-full items-center gap-2 rounded-lg px-2 py-1 text-left text-[11px] font-medium transition-colors hover:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]',
                  on ? 'text-foreground' : 'text-muted/60',
                )}
              >
                {/* Drawn as the line it actually is, dashes and all, so the
                    legend teaches the pattern the canvas uses. */}
                <svg
                  aria-hidden="true"
                  viewBox="0 0 20 4"
                  className="h-1 w-5 shrink-0 overflow-visible"
                  style={{ opacity: on ? 1 : 0.25 }}
                >
                  <line
                    x1="0"
                    y1="2"
                    x2="20"
                    y2="2"
                    stroke={meta.color}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeDasharray={meta.dash}
                  />
                </svg>
                <span className="flex-1 truncate">{meta.label}</span>
                <span className={cx('font-mono text-[10px]', on ? 'text-muted' : 'text-muted/60')}>{count}</span>
                <Icon name={on ? 'eye' : 'eyeOff'} className="h-3.5 w-3.5 shrink-0 text-muted" />
              </button>
            </li>
          )
        })}
      </ul>
      {!allOn && (
        <button type="button" onClick={onShowAll} className={cx(btn.sm, btn.ghost, 'mt-1 w-full')}>
          Show all
        </button>
      )}
      {/* Direction lives with the signals: it is the other thing a cable can
          tell you at a glance, and the same eye toggles it. */}
      <button
        type="button"
        aria-pressed={arrows}
        onClick={onToggleArrows}
        title={arrows ? 'Hide the arrowheads that show which way each signal flows' : 'Show which way each signal flows'}
        className={cx(
          'mt-1 flex w-full items-center gap-2 rounded-lg border-t border-[var(--border)] px-2 pb-1 pt-1.5 text-left text-[11px] font-medium transition-colors hover:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]',
          arrows ? 'text-foreground' : 'text-muted/60',
        )}
      >
        <Icon name="arrowRight" className="h-3.5 w-3.5 shrink-0" style={{ opacity: arrows ? 1 : 0.4 }} />
        <span className="flex-1">Direction</span>
        <Icon name={arrows ? 'eye' : 'eyeOff'} className="h-3.5 w-3.5 shrink-0 text-muted" />
      </button>
      {/* Where two cables cross, the upright one can hop over or break with
          end caps, so it is clear which line carries on where. Here for the
          same reason as Direction: it is how cables are drawn. */}
      <label
        className={cx(
          'flex w-full items-center gap-2 rounded-lg px-2 py-1 text-[11px] font-medium',
          crossings === 'off' ? 'text-muted/60' : 'text-foreground',
        )}
        title="How a cable is drawn where it crosses another"
      >
        <CrossingGlyph style={crossings} />
        <span className="flex-1">Crossings</span>
        <select
          value={crossings}
          onChange={(event) => {
            if (isCrossingStyle(event.target.value)) onCrossingsChange(event.target.value)
          }}
          className={cx(inputSm, 'py-0.5 pl-1.5 pr-1 text-[11px] text-foreground')}
        >
          {CROSSING_STYLES.map((style) => (
            <option key={style} value={style}>
              {CROSSING_LABELS[style]}
            </option>
          ))}
        </select>
      </label>
    </Panel>
  )
}

export const SignalLegend = memo(SignalLegendComponent)
